import { Block, Grid } from '@aloft/toolkit-sui';

import { useEffect, useMemo, useRef } from 'react';

import {
  setReferenceViewOpen,
  setSelectedDataSheet,
} from '../../../redux/features/referenceBarUISlice.ts';
import { selectCompSequence } from '../../../redux/features/selectors/orderSelectors.ts';
import {
  selectActiveDepreciateSeparatelyID,
  selectObjectiveEditMode,
} from '../../../redux/features/selectors/uiSelectors.ts';
import {
  setActiveDepreciateSeparatelyID,
  setObjectiveEditMode,
} from '../../../redux/features/uiSlice.ts';
import { useAppDispatch, useAppSelector } from '../../../redux/hooks.ts';
import { ColumnSection } from '../ColumnComponents/ColumnSection.tsx';
import { DepreciateSeparatelyModal } from '../ColumnSections/DepreciateSeparately/DepreciateSeparatelyModal.tsx';
import { Depreciation } from '../ColumnSections/Depreciation.tsx';
import { EffectiveAgeSlider } from '../ColumnSections/EffectiveAgeSlider/EffectiveAgeSlider.tsx';
import { QualitySlider } from '../ColumnSections/QualitySlider.tsx';
import { EDIT_MODE_OFF } from '../consts.ts';
import { ObjectiveEditModal } from '../modals/objectiveEdit/ObjectiveEditModal.tsx';
import { ViewMode } from '../types.ts';
import { useHeaderByID } from './HeaderByID.tsx';
import { useImprovementsByID } from './ImprovementsByID.tsx';
import { useSitesByID } from './SitesByID.tsx';

export const AnalysisGrid = ({ viewMode }: { viewMode: ViewMode }) => {
  const opinionOfSiteValueRef = useRef<HTMLInputElement>(null);
  const dispatch = useAppDispatch();
  const compSequence = useAppSelector(selectCompSequence);
  const objectiveEditMode = useAppSelector(selectObjectiveEditMode);
  const activeDepreciateSeparatelyID = useAppSelector(selectActiveDepreciateSeparatelyID);

  const handleDepreciateSeparatelyModeToggle = useMemo(
    () => (id: string) => {
      dispatch(setActiveDepreciateSeparatelyID(id));
    },
    [dispatch],
  );

  const HeaderByID = useHeaderByID(
    (mode) => dispatch(setObjectiveEditMode(mode)),
    objectiveEditMode,
  );
  const SitesByID = useSitesByID(viewMode, opinionOfSiteValueRef);
  const ImprovementsByID = useImprovementsByID(viewMode);

  useEffect(() => {
    if (opinionOfSiteValueRef.current) {
      opinionOfSiteValueRef.current.focus({ preventScroll: true });

      dispatch(setSelectedDataSheet('site-analysis'));
      dispatch(setReferenceViewOpen(true));
    }
  }, [dispatch]);

  return (
    <Block overflow="auto" paddingLeft="10px">
      <Grid
        className="analysis-grid"
        gridTemplateColumns={`repeat(${compSequence.length + 1}, 1fr)`}
        maxWidth="500px"
      >
        <ColumnSection
          className="analysis-grid-property-header"
          compSequence={compSequence}
          contentFill
          renderForId={HeaderByID}
        />

        <ColumnSection
          className="improvements"
          compSequence={compSequence}
          renderForId={ImprovementsByID}
        />

        <ColumnSection className="site" compSequence={compSequence} renderForId={SitesByID} />
        <ColumnSection
          className="quality-slider"
          compSequence={compSequence}
          renderForId={QualitySlider}
          backgroundColor="surface2"
        />
        <ColumnSection
          className="effective-age-slider"
          compSequence={compSequence}
          renderForId={EffectiveAgeSlider}
          backgroundColor="surface2"
          noTopBorder
        />
        <ColumnSection
          className="depreciation"
          compSequence={compSequence}
          renderForId={Depreciation}
          backgroundColor="surface2"
          noTopBorder
        />

        {objectiveEditMode !== EDIT_MODE_OFF && (
          <ObjectiveEditModal
            isModalOpen={objectiveEditMode !== EDIT_MODE_OFF}
            activeId={objectiveEditMode}
            onModalClose={() => dispatch(setObjectiveEditMode(objectiveEditMode))}
            setActiveId={(id) => dispatch(setObjectiveEditMode(id))}
            compSequence={compSequence}
          />
        )}

        {activeDepreciateSeparatelyID !== EDIT_MODE_OFF && (
          <DepreciateSeparatelyModal
            isModalOpen={activeDepreciateSeparatelyID !== EDIT_MODE_OFF}
            onModalClose={() => handleDepreciateSeparatelyModeToggle(EDIT_MODE_OFF)}
            compSequence={compSequence}
          />
        )}
      </Grid>
    </Block>
  );
};
